import { Link } from '../../../components/Link';
import {
  Lead,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('email-warmup');

/**
 * The close call flagged in docs/CONTENT-PLAN.md §2: "email warmup" is both an informational and a
 * commercial term, and /features/warmup/ already owns the commercial half. This page therefore
 * stays strictly on HOW warming works and WHEN IT DOES NOTHING — the honest half no vendor page
 * will write. The near-duplicate guard is the check that the two have not converged.
 */
export default function EmailWarmupPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'Warmup is raising sending volume gradually from a new domain or IP so mailbox providers can form an opinion before you send at scale. It works because providers treat unknown senders cautiously and sudden volume as suspicious. It fixes being unknown. It does not fix being unwanted.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="What warming actually does">
        <p>
          A provider seeing mail from a domain it has never encountered has no basis for a decision.
          It is cautious by default — and a brand-new domain that sends fifty thousand messages on
          its first day looks exactly like a domain bought that morning for a spam run, because that
          is a thing that happens constantly.
        </p>
        <p>
          Warming is the process of not looking like that. You send a small, growing volume to
          people who engage, and the provider accumulates enough evidence to give you the benefit of
          the doubt later. It is{' '}
          <Link to="/learn/sender-reputation" className="text-accent underline underline-offset-4">
            reputation building
          </Link>{' '}
          under a different name, applied to a sender starting from zero rather than digging out of
          a hole.
        </p>
      </LearnSection>

      <LearnSection title="A realistic schedule">
        <p>
          Volumes below are per provider per day, not totals — Gmail forms its own opinion
          independently of Outlook. Treat them as a shape rather than a prescription; the rule that
          matters is the last column.
        </p>
        <LearnTable
          head={['Week', 'Per provider, per day', 'Send to', 'Watch']}
          rows={[
            ['1', '20–50', 'Your most engaged contacts only', 'Any bounce at all — stop and investigate'],
            ['2', '50–150', 'Still the top engagement tier', 'Complaint rate; it should be zero at this size'],
            ['3', '150–500', 'Widening in engagement order', 'Deferrals — a 4xx means slow down'],
            ['4', '500–2,000', 'Widening', 'Spam placement, not just delivery'],
            ['5–8', 'Roughly double weekly', 'Full engaged segment', <Lead key="a">Any degradation → hold, do not push through</Lead>],
          ]}
        />
        <p>
          Four to eight weeks to reach meaningful volume. Anyone promising a warm domain in a week
          is describing volume, not reputation.
        </p>
        <p>
          <Lead>Consistency beats the numbers.</Lead> A steady 200 a day is read as a legitimate
          sender. Two thousand on Monday and nothing until Friday is read as a compromised account,
          at the same weekly total. If you have to choose between hitting the schedule and sending
          evenly, send evenly.
        </p>
      </LearnSection>

      <LearnSection title="When warmup is the wrong tool">
        <p>
          The part vendor pages skip. Warmup solves exactly one problem — being unknown. If your
          problem is something else, warming will consume six weeks and change nothing.
        </p>
        <LearnTable
          head={['Situation', 'Will warming help?', 'What actually fixes it']}
          rows={[
            ['New domain, no history', <Lead key="a">Yes — this is the case it exists for</Lead>, '—'],
            ['New dedicated IP at real volume', <Lead key="b">Yes</Lead>, '—'],
            [
              'Authentication failing',
              'No',
              <Link key="c" to="/learn/email-authentication" className="text-accent underline underline-offset-4">Fix SPF, DKIM and DMARC first</Link>,
            ],
            [
              'Purchased or scraped list',
              'No — it makes it worse',
              'Stop sending to it. Warming a bad list trains providers to distrust you',
            ],
            [
              'Existing domain with a complaint problem',
              'No',
              <Link key="d" to="/learn/sender-reputation" className="text-accent underline underline-offset-4">Suppress disengaged recipients</Link>,
            ],
            [
              'Listed on a list receivers consult',
              'No',
              <Link key="e" to="/learn/spamhaus-listing" className="text-accent underline underline-offset-4">Find the sub-list and fix the cause</Link>,
            ],
            ['Low volume on a shared ESP IP', 'Largely unnecessary', 'The pool already has history'],
          ]}
        />
        <p>
          The purchased-list row is the one to take seriously. Warming <em>into</em> a bad list does
          not gently introduce you — it methodically demonstrates, over six weeks, that mail from
          your domain generates complaints. You end up worse than if you had sent it all at once,
          because now the pattern is established.
        </p>
      </LearnSection>

      <LearnSection title="Automated warmup pools, honestly">
        <p>
          Warmup services connect mailboxes that send each other messages and mark them as important,
          manufacturing engagement signals. They are widely used and worth understanding rather than
          dismissing or oversubscribing to.
        </p>
        <LearnSubsection title="What they genuinely do">
          <p>
            They generate positive interaction — opens, replies, rescues from spam — on a schedule,
            without you needing an engaged audience yet. For a genuinely new domain with nobody to
            send to, that is a real answer to a real chicken-and-egg problem, and it is why the
            category exists.
          </p>
        </LearnSubsection>
        <LearnSubsection title="What to be clear-eyed about">
          <LearnList>
            <li>
              The signals are synthetic. Providers are aware of the pattern, and detection improves.
              A pool that is too regular or too dense looks like what it is.
            </li>
            <li>
              It does not transfer. Engagement from pool mailboxes tells a provider that pool
              mailboxes like your mail. Your actual recipients are a separate population, and the
              real test starts when you send to them.
            </li>
            <li>
              It cannot outrun a bad list. Nothing in this article can.
            </li>
          </LearnList>
        </LearnSubsection>
        <p>
          Used as a bridge — carrying a new domain to the point where real engagement takes over —
          it does its job. Used as a permanent substitute for people who want your mail, it is
          maintaining a number rather than a reputation. Ours{' '}
          <Link to="/features/warmup" className="text-accent underline underline-offset-4">
            runs on that assumption
          </Link>
          .
        </p>
      </LearnSection>

      <LearnSection title="Before you start">
        <LearnList ordered>
          <li>
            <Lead>Get authentication right first.</Lead> Warming a domain that fails{' '}
            <Link to="/learn/dmarc-alignment" className="text-accent underline underline-offset-4">
              DMARC alignment
            </Link>{' '}
            builds a reputation for sending unauthenticated mail, which is not the reputation you
            wanted.
          </li>
          <li>
            <Lead>Decide whether you need it at all.</Lead> Under roughly 100k a month on a shared
            ESP IP, the pool’s history is doing most of the work already.
          </li>
          <li>
            <Lead>Clean the list before, not after.</Lead> Every hard bounce during warmup costs
            more than it would later, because you have no history to absorb it.
          </li>
          <li>
            <Lead>Warm the subdomain you will actually send from.</Lead> Reputation attaches per
            domain, so warming the root and then sending from a subdomain starts you at zero again.
          </li>
          <li>
            <Lead>Have somewhere to watch it.</Lead> Google Postmaster Tools needs a few hundred
            messages a day before it reports anything, which is roughly week three.
          </li>
        </LearnList>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'How long does email warmup take?',
    a: 'Four to eight weeks to reach meaningful volume from a genuinely new domain. Reputation is a moving average over weeks, so there is no way to compress it. Anyone offering a warm domain in a week is describing volume, not reputation.',
  },
  {
    q: 'Do I need to warm up if I use a shared ESP IP?',
    a: 'Much less than you would think. The shared pool already has history with every provider, so the IP half is handled. Your domain still starts unknown, so a gradual ramp is worth doing — but the full protocol matters far more on a dedicated IP.',
  },
  {
    q: 'Do automated warmup pools actually work?',
    a: 'They generate genuine positive signals and solve a real chicken-and-egg problem for a domain with no audience yet. But the engagement is synthetic and does not transfer to your actual recipients, and no amount of it survives a bad list. Treat it as a bridge, not a destination.',
  },
  {
    q: 'Can I warm up a domain that already has a bad reputation?',
    a: 'Warming is for unknown senders, not damaged ones. For an existing domain the fix is to suppress disengaged recipients, send only to your most engaged segment for several weeks, and let the moving average recover. The sequence looks similar; the starting point is not.',
  },
  {
    q: 'Should I warm the root domain or a subdomain?',
    a: 'Whichever you will actually send from, because reputation attaches per domain. Warming the root and then sending marketing from a subdomain starts you at zero. Sending bulk from a subdomain is good practice anyway — it keeps campaign complaints away from your corporate mail.',
  },
  {
    q: 'What happens if I skip warmup entirely?',
    a: 'On a new domain at volume, expect deferrals within hours and spam placement or outright refusal shortly after. Providers read sudden volume from an unknown sender as a spam run, because it usually is. Recovering from that costs longer than warming would have.',
  },
];
